// Rate-quote results table — shared by admin (shows carrier_cost) and client
// (price ONLY, cost censored server-side and never rendered).
//
// Rates arrive already priced; we sort cheapest-first by the client price and
// badge the cheapest row. The "Estimate — final billed cost may differ" note
// is always shown so neither surface treats a quote as a locked figure.

import { Info } from 'lucide-react'

function fmtMoney(amount, currency) {
  if (amount == null) return '—'
  try {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: currency || 'AUD',
    }).format(amount)
  } catch {
    // Unknown currency code → fall back to a plain 2dp figure + raw code.
    return `${Number(amount).toFixed(2)} ${currency || ''}`.trim()
  }
}

export default function RateResultsTable({ rates = [], errors = [], showCost = false }) {
  // Sort cheapest-first on the price the surface actually cares about (the
  // client price on both admin and client surfaces).
  const sorted = [...rates].sort((a, b) => (a.price ?? Infinity) - (b.price ?? Infinity))

  return (
    <div className="space-y-3">
      {errors.length > 0 && (
        <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          <p className="font-medium mb-1">Some carriers couldn't be quoted:</p>
          <ul className="list-disc list-inside space-y-0.5 text-xs">
            {errors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </div>
      )}

      {sorted.length === 0 ? (
        <div className="text-sm text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-4 py-6 text-center">
          No rates returned for this shipment.
        </div>
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                <th className="px-4 py-2.5">Carrier</th>
                <th className="px-4 py-2.5">Service</th>
                <th className="px-4 py-2.5 text-center">Delivery</th>
                {showCost && <th className="px-4 py-2.5 text-right">Carrier cost</th>}
                <th className="px-4 py-2.5 text-right">{showCost ? 'Client price' : 'Price'}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {sorted.map((r, i) => (
                <tr key={i} className={i === 0 ? 'bg-emerald-50/40' : ''}>
                  <td className="px-4 py-2.5 font-medium text-slate-800">
                    <div className="flex items-center gap-2">
                      {r.carrier || '—'}
                      {i === 0 && (
                        <span className="badge bg-emerald-100 text-emerald-700">Cheapest</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-slate-600">{r.service || '—'}</td>
                  <td className="px-4 py-2.5 text-center text-slate-600 tabular-nums">
                    {r.delivery_days != null ? `${r.delivery_days} day${r.delivery_days === 1 ? '' : 's'}` : '—'}
                  </td>
                  {showCost && (
                    <td className="px-4 py-2.5 text-right text-slate-500 tabular-nums">
                      {fmtMoney(r.carrier_cost, r.currency)}
                    </td>
                  )}
                  <td className="px-4 py-2.5 text-right font-semibold text-slate-800 tabular-nums">
                    {fmtMoney(r.price, r.currency)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {sorted.length > 0 && (
        <p className="flex items-center gap-1.5 text-xs text-slate-400">
          <Info size={13} />
          Estimate — final billed cost may differ.
        </p>
      )}
    </div>
  )
}
